import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Star, BadgeCheck, Filter, RefreshCw, ExternalLink } from "lucide-react";
import { useEnquiry } from "./EnquiryContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Permanent reviews from carehome.co.uk - these always display
const CAREHOME_REVIEWS = [
  {
    id: "carehome-1",
    reviewer_name: "Sarah M.",
    relationship: "Daughter of Resident",
    rating: 10,
    review_title: "Exceptional Care for My Mother",
    review_text: "The staff at Barnston Court have been absolutely wonderful with my mother. From day one, they made her feel at home and treated her with such dignity and respect. The carers know her by name and understand her needs perfectly. I can't thank them enough for the peace of mind they've given our family.",
    care_type: "dementia",
    stay_duration: "18 months",
    created_at: "2024-11-15",
    source: "carehome.co.uk",
    isPermanent: true
  },
  {
    id: "carehome-2",
    reviewer_name: "John T.",
    relationship: "Son of Resident",
    rating: 9,
    review_title: "Professional and Compassionate Team",
    review_text: "Dad has been at Barnston Court for over a year now. The nursing team is highly professional and the care he receives is first-class. They keep us informed about his health and involve us in all decisions. The home is always clean and welcoming.",
    care_type: "nursing",
    stay_duration: "14 months",
    created_at: "2024-10-22",
    source: "carehome.co.uk",
    isPermanent: true
  },
  {
    id: "carehome-3",
    reviewer_name: "Margaret H.",
    relationship: "Daughter of Resident",
    rating: 10,
    review_title: "Couldn't Ask for Better Care",
    review_text: "Mum moved to Barnston Court after a hospital stay and we were worried about the transition. The staff made it so smooth and she settled in beautifully. The activities programme keeps her engaged and she's made lovely friends. The food is excellent too - proper home cooking!",
    care_type: "residential",
    stay_duration: "8 months",
    created_at: "2024-09-18",
    source: "carehome.co.uk",
    isPermanent: true
  },
  {
    id: "carehome-4",
    reviewer_name: "David R.",
    relationship: "Husband of Resident",
    rating: 9,
    review_title: "A True Home from Home",
    review_text: "My wife has dementia and finding the right care home was so stressful. Barnston Court stood out from the start. The dementia unit is purpose-built and the staff have specialist training. She's calmer and happier than she's been in years. I visit daily and always feel welcome.",
    care_type: "dementia",
    stay_duration: "2 years",
    created_at: "2024-08-05",
    source: "carehome.co.uk",
    isPermanent: true
  },
  {
    id: "carehome-5",
    reviewer_name: "Patricia L.",
    relationship: "Niece of Resident",
    rating: 10,
    review_title: "Outstanding End of Life Care",
    review_text: "My aunt spent her final months at Barnston Court and I cannot speak highly enough of the care she received. The staff were so gentle and compassionate, not just with her but with our whole family. They gave her dignity right to the end and supported us through everything.",
    care_type: "nursing",
    stay_duration: "4 months",
    created_at: "2024-07-12",
    source: "carehome.co.uk",
    isPermanent: true
  }
];

export const Reviews = () => {
  const { openEnquiryModal } = useEnquiry();
  const [filter, setFilter] = useState("most-recent");
  const [careFilter, setCareFilter] = useState("all");
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApprovedReviews();
  }, []);

  const fetchApprovedReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews/approved`);
      setUserReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setUserReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // Combine permanent carehome reviews with user-submitted approved reviews
  const allReviews = [...CAREHOME_REVIEWS, ...userReviews];

  const filteredReviews = allReviews
    .filter(review => careFilter === "all" || review.care_type === careFilter)
    .sort((a, b) => {
      if (filter === "highest") return b.rating - a.rating;
      if (filter === "lowest") return a.rating - b.rating;
      // Most recent - sort by created_at
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <section id="reviews" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Families Say About Barnston Court
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Honest feedback from families who have experienced our care firsthand
          </p>
        </div>

        {/* Filters */}
        <div className="max-w-6xl mx-auto mb-8">
          <Card className="border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-teal-600" />
                  <span className="font-semibold text-gray-700">Filter by:</span>
                </div>
                
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="most-recent">Most Recent</SelectItem>
                    <SelectItem value="highest">Highest Rated</SelectItem>
                    <SelectItem value="lowest">Lowest Rated</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={careFilter} onValueChange={setCareFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Care type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Care Types</SelectItem>
                    <SelectItem value="nursing">Nursing Care</SelectItem>
                    <SelectItem value="dementia">Dementia Care</SelectItem>
                    <SelectItem value="end-of-life">End of Life Care</SelectItem>
                    <SelectItem value="bariatric">Bariatric Care</SelectItem>
                  </SelectContent>
                </Select>

                <div className="ml-auto">
                  <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-200">
                    {filteredReviews.length} Reviews
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
            <p className="text-gray-600">Loading reviews...</p>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center py-12">
            <Card className="border-2 border-gray-200">
              <CardContent className="pt-8 pb-8">
                <p className="text-gray-600 mb-4">No reviews yet. Be the first to share your experience!</p>
                <Button
                  onClick={() => {
                    const element = document.getElementById("leave-review");
                    if (element) element.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  Leave a Review
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {filteredReviews.map((review) => (
              <Card
                key={review.id}
                className={`border-none shadow-lg hover:shadow-xl transition-shadow duration-300 ${
                  review.isPermanent ? 'ring-2 ring-amber-200' : ''
                }`}
              >
                <CardContent className="pt-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-gray-900">{review.reviewer_name}</h3>
                        {review.isPermanent ? (
                          <Badge className="bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1 text-xs">
                            <ExternalLink className="h-3 w-3" />
                            carehome.co.uk
                          </Badge>
                        ) : (
                          <Badge className="bg-teal-600 text-white hover:bg-teal-700 flex items-center gap-1 text-xs">
                            <BadgeCheck className="h-3 w-3" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{review.relationship}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-1 mb-1">
                        {[...Array(10)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${
                              i < review.rating
                                ? "fill-orange-500 text-orange-500"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-orange-600">{review.rating}/10</span>
                    </div>
                  </div>

                  {/* Review Title */}
                  <h4 className="font-semibold text-lg text-gray-900 mb-3">{review.review_title}</h4>

                  {/* Review Text */}
                  <p className="text-gray-700 leading-relaxed mb-4">{review.review_text}</p>

                  {/* Footer */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pt-4 border-t border-gray-100">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                      {review.care_type ? review.care_type.charAt(0).toUpperCase() + review.care_type.slice(1) : ''} Care
                    </span>
                    <span>•</span>
                    <span>Stay: {review.stay_duration}</span>
                    <span>•</span>
                    <span>{formatDate(review.created_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12 space-y-6">
          {/* Google Review CTA — helps boost local Wirral search ranking */}
          <Card className="max-w-2xl mx-auto border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-center justify-center gap-2 mb-3">
                {/* Google multicolour "G" */}
                <svg className="h-8 w-8" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                <h3 className="text-2xl font-bold text-gray-900">
                  Loved Your Experience?
                </h3>
              </div>
              <p className="text-gray-700 mb-6">
                Share your story on Google to help other families discover the care and warmth
                of Barnston Court. Your review takes less than a minute and means the world to us.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                data-testid="google-review-btn"
              >
                <a
                  href="https://www.google.com/search?q=Barnston+Court+Nursing+Home+21+Barnston+Lane+Moreton+Wirral+CH46+7TN"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Star className="h-4 w-4 mr-2 fill-current" />
                  Leave a Google Review
                  <ExternalLink className="h-3.5 w-3.5 ml-2" />
                </a>
              </Button>
              <p className="text-xs text-gray-500 mt-4">
                You'll be taken to Google to sign in and post your review. Nothing you write here
                is stored on our website.
              </p>
            </CardContent>
          </Card>

          {/* Original CTA */}
          <Card className="max-w-2xl mx-auto border-2 border-orange-200 bg-orange-50">
            <CardContent className="pt-8 pb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Experience the Care Yourself
              </h3>
              <p className="text-gray-700 mb-6">
                Book a visit to meet our team and see why families rate us so highly
              </p>
              <Button
                onClick={() => openEnquiryModal("visit")}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="reviews-book-visit-btn"
              >
                Book Your Visit Today
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
